import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { Box, Button } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import ArrowDownwardRounded from '@mui/icons-material/ArrowDownwardRounded';

/**
 * Widest a page-width conversation gets: comfortable lines of body text, with
 * room for an inline table. Anything that must line up with the conversation
 * — the composer below it — uses the same width.
 */
export const CHAT_READING_WIDTH = 800;
/** How close to the end still counts as "at the bottom", px. */
const AT_BOTTOM_SLACK = 40;

export interface ChatMessageListProps {
  children: React.ReactNode;
  /**
   * Anything that changes when new content arrives. When it changes, the list
   * follows to the end only if the reader was already there; otherwise it
   * offers "Jump to latest" and leaves them where they are.
   */
  followKey: unknown;
  /** Change it to jump to the end regardless — for instance when the person sends. */
  scrollToEndKey?: unknown;
  /** `page` centres the conversation at a reading width; `docked` fills a narrow panel. */
  variant?: 'page' | 'docked';
  /** Said once to screen readers, e.g. "Answer ready" — not every word as it arrives. */
  announcement?: string;
}

/**
 * The scrolling conversation. It never pulls someone down while they read
 * earlier messages: it only follows new content when they are already at the
 * bottom.
 */
export function ChatMessageList({
  children,
  followKey,
  scrollToEndKey,
  variant = 'docked',
  announcement,
}: ChatMessageListProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const atBottom = useRef(true);
  const [showJump, setShowJump] = useState(false);

  const toEnd = useCallback(() => {
    const el = ref.current;
    if (el) el.scrollTop = el.scrollHeight;
    atBottom.current = true;
    setShowJump(false);
  }, []);

  const handleScroll = () => {
    const el = ref.current;
    if (!el) return;
    atBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < AT_BOTTOM_SLACK;
    if (atBottom.current) setShowJump(false);
  };

  useLayoutEffect(() => {
    if (atBottom.current) toEnd();
    else setShowJump(true);
  }, [followKey, toEnd]);

  useLayoutEffect(() => {
    toEnd();
  }, [scrollToEndKey, toEnd]);

  return (
    <Box sx={{ position: 'relative', flex: 1, minHeight: 0 }}>
      <Box
        ref={ref}
        onScroll={handleScroll}
        role="log"
        aria-live="off"
        aria-label="Conversation"
        sx={{
          position: 'absolute',
          inset: 0,
          overflowY: 'auto',
          px: 2,
          py: 2.5,
          // Fade the last few pixels, so text meeting the composer reads as
          // "more below" rather than cut off. A mask sets opacity, not colour.
          maskImage: 'linear-gradient(to bottom, currentColor calc(100% - 20px), transparent)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2.25,
            minHeight: '100%',
            width: '100%',
            maxWidth: variant === 'page' ? CHAT_READING_WIDTH : 'none',
            mx: 'auto',
          }}
        >
          {children}
        </Box>
      </Box>
      {showJump && (
        <Button
          size="small"
          variant="outlined"
          color="inherit"
          onClick={toEnd}
          startIcon={<ArrowDownwardRounded fontSize="small" />}
          sx={(t) => ({
            position: 'absolute',
            left: '50%',
            bottom: t.spacing(1.5),
            transform: 'translateX(-50%)',
            borderRadius: `${t.ds.radius.full}px`,
            bgcolor: t.tokens.surface.raised,
            borderColor: t.tokens.border.default,
            boxShadow: t.tokens.shadow.md,
            '&:hover': { bgcolor: t.tokens.surface.raised, borderColor: t.tokens.border.strong },
          })}
        >
          Jump to latest
        </Button>
      )}
      <Box style={visuallyHidden} aria-live="polite">{announcement}</Box>
    </Box>
  );
}
